import React, { useMemo, useState } from "react";
import { Popover } from "@mui/material";
import { FiPhone, FiMessageSquare } from "react-icons/fi";
import { FaWhatsapp } from "react-icons/fa";
import {
  buildPayableReminderMessage,
  buildReceivableReminderMessage,
  buildSmsUrl,
  buildTelUrl,
  buildWhatsAppUrl,
} from "../../utils/phoneLinks";

const actionClass =
  "inline-flex items-center justify-center gap-1 rounded-lg border px-2.5 py-1.5 text-xs font-semibold transition-colors";

const iconActionClass =
  "inline-flex items-center justify-center w-9 h-9 rounded-full border transition-all duration-200 shadow-soft hover:scale-105 active:scale-95";

const ACTION_LABELS = {
  call: "Call",
  whatsapp: "WhatsApp",
  sms: "SMS",
};

const normalizePhone = (value) => {
  if (value === null || value === undefined) return "";
  return String(value).trim();
};

const getContactOptions = (customer) => {
  const options = [];
  const contact = normalizePhone(customer?.contact);
  const contact2 = normalizePhone(customer?.contact2);

  if (contact) {
    options.push({ key: "contact", label: "Contact 1", number: contact });
  }
  if (contact2) {
    options.push({ key: "contact2", label: "Contact 2", number: contact2 });
  }
  return options;
};

const CustomerContactActions = ({
  customer,
  compact = false,
  variant = "default",
  reminderType = "receivable",
}) => {
  const contactOptions = useMemo(() => getContactOptions(customer), [customer]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerAction, setPickerAction] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);

  if (contactOptions.length === 0) {
    return <span className="text-xs text-muted italic">No phone</span>;
  }

  const message =
    reminderType === "payable"
      ? buildPayableReminderMessage(customer)
      : buildReceivableReminderMessage(customer);

  const hasMultiple = contactOptions.length > 1;

  const runAction = (action, number) => {
    if (action === "call") {
      window.location.href = buildTelUrl(number);
      return;
    }
    if (action === "whatsapp") {
      window.open(buildWhatsAppUrl(number, message), "_blank", "noopener,noreferrer");
      return;
    }
    if (action === "sms") {
      window.location.href = buildSmsUrl(number, message);
    }
  };

  const closePicker = () => {
    setPickerOpen(false);
    setPickerAction(null);
    setAnchorEl(null);
  };

  const handleAction = (e, action) => {
    e.stopPropagation();
    e.preventDefault();

    if (hasMultiple) {
      setAnchorEl(e.currentTarget);
      setPickerAction(action);
      setPickerOpen(true);
      return;
    }

    runAction(action, contactOptions[0].number);
  };

  const handlePickNumber = (e, number) => {
    e.stopPropagation();
    if (!pickerAction) return;
    runAction(pickerAction, number);
    closePicker();
  };

  const stop = (e) => e.stopPropagation();

  const renderButtons = () => {
    const buttons = [
      {
        id: "call",
        title: hasMultiple ? "Choose number to call" : "Call",
        className:
          variant === "icon"
            ? `${iconActionClass} border-success/20 bg-successSoft text-success hover:bg-successSoft`
            : `${actionClass} border-success/20 bg-successSoft text-success hover:bg-successSoft`,
        icon: <FiPhone size={variant === "icon" ? 15 : 14} />,
        label: "Call",
      },
      {
        id: "whatsapp",
        title: hasMultiple ? "Choose number for WhatsApp" : "WhatsApp",
        className:
          variant === "icon"
            ? `${iconActionClass} border-success/20 bg-successSoft text-success hover:bg-successSoft`
            : `${actionClass} border-success/20 bg-successSoft text-success hover:bg-successSoft`,
        icon: <FaWhatsapp size={variant === "icon" ? 15 : 14} />,
        label: "WhatsApp",
      },
      {
        id: "sms",
        title: hasMultiple ? "Choose number for SMS" : "SMS",
        className:
          variant === "icon"
            ? `${iconActionClass} border-main/20 bg-brandSoft text-main hover:bg-brandSoft`
            : `${actionClass} border-main/20 bg-brandSoft text-main hover:bg-brandSoft`,
        icon: <FiMessageSquare size={variant === "icon" ? 15 : 14} />,
        label: "SMS",
      },
    ];

    return buttons.map(({ id, title, className, icon, label }) => (
      <button
        key={id}
        type="button"
        title={title}
        className={className}
        onClick={(e) => handleAction(e, id)}
      >
        {icon}
        {variant !== "icon" && !compact && label}
      </button>
    ));
  };

  return (
    <>
      <div
        className={`flex items-center gap-1.5 ${compact ? "" : variant === "default" ? "flex-wrap gap-2 mt-1" : ""}`}
        onClick={stop}
      >
        {renderButtons()}
      </div>

      <Popover
        open={pickerOpen}
        anchorEl={anchorEl}
        onClose={closePicker}
        onClick={stop}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        transformOrigin={{ vertical: "bottom", horizontal: "center" }}
        slotProps={{
          paper: {
            sx: {
              borderRadius: "12px",
              minWidth: 240,
              maxWidth: 320,
              boxShadow: "0 10px 40px rgba(0,0,0,0.12)",
              overflow: "hidden",
            },
          },
        }}
      >
        <div className="p-3">
          <p className="text-xs font-bold text-muted uppercase tracking-wide mb-2">
            {pickerAction ? `Choose number — ${ACTION_LABELS[pickerAction]}` : "Choose number"}
          </p>
          <div className="flex flex-col gap-1.5">
            {contactOptions.map(({ key, label, number }) => (
              <button
                key={key}
                type="button"
                onClick={(e) => handlePickNumber(e, number)}
                className="flex flex-col items-start w-full text-left px-3 py-2.5 rounded-lg border border-line hover:border-main/30 hover:bg-brandSoft transition-colors"
              >
                <span className="text-[10px] font-bold uppercase text-muted">{label}</span>
                <span className="text-sm font-semibold text-ink tabular-nums">{number}</span>
              </button>
            ))}
          </div>
        </div>
      </Popover>
    </>
  );
};

export default CustomerContactActions;
